// Para o formulário "Minha empresa" do sistema: ao completar um CNPJ válido, busca os dados
// na Receita Federal e confere a inscrição estadual no cadastro da SEFAZ.
//
// Uso (o formulário é do sistema; este componente só busca, avisa e devolve os dados):
//   <BuscaCnpjEmpresa
//     cnpj={form.cnpj}
//     inscricaoEstadual={form.ie}
//     onPreencher={(d) => setForm((f) => ({ ...f, ...preencherVazios(f, d) }))}
//     onUsarIsento={() => setForm((f) => ({ ...f, ie: "ISENTO" }))}
//     enderecoAtual={form.endereco}            // opcional: compara com a Receita
//     onSubstituirEndereco={(end) => setForm((f) => ({ ...f, endereco: end }))}
//   />
// `d` traz: razao_social, nome_fantasia, inscricao_estadual, email, telefone, regime_sugerido
// ("Simples Nacional" | "MEI" | null), cnae_principal (só dígitos, ex.: "5611203"), cnae_descricao e endereco {cep, logradouro, numero, complemento, bairro,
// municipio, codigo_municipio (IBGE), uf}. Preencha só os campos que estiverem vazios.
import React, { useEffect, useRef, useState } from "react";
import { fiscal } from "../api";
import { documentoValido, soDigitos } from "../utils";

const norm = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\W/g, "").toUpperCase();

export default function BuscaCnpjEmpresa({ cnpj, inscricaoEstadual, onPreencher, onUsarIsento, enderecoAtual, onSubstituirEndereco }) {
  const [enderecoReceita, setEnderecoReceita] = useState(null);
  const [estado, setEstado] = useState(null); // {tipo, texto}
  const [verificacao, setVerificacao] = useState(null);
  const [uf, setUf] = useState("");
  const ultimo = useRef("");

  useEffect(() => {
    const doc = soDigitos(cnpj);
    if (doc.length !== 14 || !documentoValido(doc) || doc === ultimo.current) return;
    ultimo.current = doc;
    setEstado({ tipo: "info", texto: "Consultando a Receita Federal e a SEFAZ…" });
    fiscal.consultarEmpresa(doc).then((r) => {
      const e = r.endereco || {};
      setUf(e.uf || "");
      setVerificacao(r.ie_verificacao || null);
      const endereco = { cep: e.cep || "", logradouro: e.logradouro || "", numero: e.numero || "", complemento: e.complemento || "",
        bairro: e.bairro || "", municipio: e.municipio || "", codigo_municipio: e.codigo_municipio || "", uf: e.uf || "" };
      setEnderecoReceita(endereco);
      onPreencher && onPreencher({
        razao_social: r.nome || "", nome_fantasia: r.nome_fantasia || "", inscricao_estadual: r.inscricao_estadual || "",
        email: r.email || "", telefone: e.telefone || "", regime_sugerido: r.regime_sugerido || null,
        cnae_principal: r.cnae_principal || "", cnae_descricao: r.cnae_descricao || "", endereco,
      });
      const sit = r.situacao_cadastral && !/ativa/i.test(r.situacao_cadastral) ? ` Situação na Receita: ${r.situacao_cadastral}.` : "";
      setEstado({ tipo: sit ? "atencao" : "sucesso", texto: `Dados preenchidos pela Receita Federal. Confira.${sit}` });
    }).catch((err) => setEstado({ tipo: "erro",
      texto: err.status === 404 ? "CNPJ não encontrado na Receita Federal." : `Não foi possível consultar o CNPJ: ${err.message}` }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpj]);

  const ie = String(inscricaoEstadual || "").trim();
  const temIe = ie.toUpperCase() === "ISENTO" || soDigitos(ie).length > 0;
  const cor = { info: "text-gray-600", sucesso: "text-emerald-700", atencao: "text-amber-700", erro: "text-red-700" };

  const a = enderecoAtual || {};
  const rec = enderecoReceita || {};
  const enderecoDiferente = enderecoReceita && onSubstituirEndereco && (a.cep || a.logradouro || a.municipio) &&
    ["cep", "logradouro", "numero", "municipio", "uf"].some((k) => norm(a[k]) !== norm(rec[k]));
  const texto = (x) => [x.logradouro, x.numero, x.complemento, x.bairro, x.municipio && `${x.municipio}/${x.uf}`, x.cep && `CEP ${x.cep}`]
    .filter(Boolean).join(", ");

  return (
    <div className="space-y-2 text-sm">
      {estado && <p className={cor[estado.tipo]}>{estado.texto}</p>}
      {enderecoDiferente && (
        <div className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-blue-900">
          <p><strong>O endereço é diferente do que está na Receita Federal.</strong></p>
          <p>Cadastrado: {texto(a) || "—"}</p>
          <p>Na Receita: {texto(rec)}</p>
          <p className="text-xs">A nota sai com o endereço cadastrado aqui. Use o da Receita, a menos que ela esteja desatualizada.</p>
          <button type="button" className="mt-1 rounded-md bg-blue-600 px-3 py-1 text-white"
            onClick={() => onSubstituirEndereco(rec)}>Usar o endereço da Receita</button>
        </div>
      )}
      {verificacao === "sefaz_com_ie" && temIe && (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-emerald-800">✓ Inscrição estadual encontrada na SEFAZ{uf && `-${uf}`} e preenchida. Confira.</p>
      )}
      {verificacao === "sefaz_sem_ie" && !temIe && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
          SEFAZ{uf && `-${uf}`}: não há inscrição estadual ativa para este CNPJ. Para emitir NF-e/NFC-e a empresa precisa de IE
          (peça ao contador). Se for dispensada de IE, use ISENTO.
          {onUsarIsento && <button type="button" className="ml-2 underline" onClick={onUsarIsento}>Usar ISENTO</button>}
        </div>
      )}
      {verificacao !== "sefaz_com_ie" && verificacao !== "sefaz_sem_ie" && soDigitos(cnpj).length === 14 && !temIe && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
          <strong>Informe a inscrição estadual.</strong> Não foi possível confirmar na SEFAZ{uf && `-${uf}`}. Ela está no cartão CNPJ/alvará
          ou com o contador. Se a empresa não tiver IE, digite ISENTO. Sem isso a emissão de notas fica bloqueada.
          {onUsarIsento && <button type="button" className="ml-2 underline" onClick={onUsarIsento}>Não tem IE (usar ISENTO)</button>}
        </div>
      )}
    </div>
  );
}
